/**
 * Authentication UI Components
 * Components for handling different authentication states and user interactions
 */

import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Card, Typography, Alert, Spin, Space, Result } from 'antd';
import { 
  LoginOutlined, 
  LoadingOutlined, 
  ExclamationCircleOutlined,
  UserDeleteOutlined 
} from '@ant-design/icons';
import { getLoginConfig, logoutConfig } from '../auth0Config';
import { useAuth } from '../AuthContext';

const { Title, Text, Paragraph } = Typography;

/**
 * Main Sign In Component
 * Displays the login page with security notice
 */
export const SignIn: React.FC = () => {
  const { loginWithRedirect } = useAuth0();

  const handleLogin = (): void => {
    loginWithRedirect(getLoginConfig());
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 20
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 450, 
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          borderRadius: 12
        }}
        bodyStyle={{ padding: 40 }}
      >
        <Title level={2} style={{ marginBottom: 10, color: '#1890ff' }}>
          LifeScience Implementations Portal
        </Title>

        <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 30 }}>
          Secure access to internal tooling
        </Text>
        
        <Alert
          message="Security Notice"
          description={
            <div>
              <Paragraph style={{ marginBottom: 8 }}>
                This application may contain sensitive data and proprietary information.
              </Paragraph>
              <Text>
                • Only authorized users may access this system<br/>
                • All activities are logged and monitored<br/>
                • Ensure you are authorized before proceeding
              </Text>
            </div>
          }
          type="info"
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 30, textAlign: 'left' }}
        />
        
        <Button 
          type="primary" 
          size="large" 
          icon={<LoginOutlined />}
          onClick={handleLogin}
          block
          style={{ 
            height: 48,
            fontSize: 16,
            fontWeight: 600,
            borderRadius: 6
          }}
        >
          Sign In with SSO
        </Button>
        
        <Text 
          type="secondary" 
          style={{ 
            display: 'block', 
            marginTop: 20, 
            fontSize: 12 
          }}
        >
          By signing in, you agree to our terms of use and privacy policy
        </Text>
      </Card>
    </div>
  );
};

/**
 * Loading component for Auth0 authentication process
 */
export const PendingAuthentication: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    flexDirection: 'column',
    background: '#f5f5f5'
  }}>
    <Space direction="vertical" align="center" size="large">
      <Spin 
        indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} />} 
        size="large" 
      />
      <div style={{ textAlign: 'center' }}>
        <Title level={3} style={{ margin: 0, color: '#595959' }}>
          Authenticating User
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Please wait while we verify your credentials...
        </Text>
      </div>
    </Space>
  </div>
);

/**
 * Loading component for user authorization (profile fetch)
 */
export const PendingAuthorization: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    flexDirection: 'column',
    background: '#f5f5f5'
  }}>
    <Space direction="vertical" align="center" size="large">
      <Spin 
        indicator={<LoadingOutlined style={{ fontSize: 48, color: '#52c41a' }} />} 
        size="large" 
      />
      <div style={{ textAlign: 'center' }}>
        <Title level={3} style={{ margin: 0, color: '#595959' }}>
          Authorizing User Profile
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Loading your profile and permissions...
        </Text>
      </div>
    </Space>
  </div>
);

/**
 * Error component for authentication failures
 */
export const AuthError: React.FC<{ error?: string }> = ({ error }) => {
  const { loginWithRedirect } = useAuth0();

  const handleRetry = (): void => {
    loginWithRedirect(getLoginConfig());
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: '#f5f5f5',
      padding: 20
    }}>
      <Result
        status="error"
        title="Authentication Failed"
        subTitle={error || "An error occurred during the authentication process. Please try again."}
        extra={[
          <Button type="primary" key="retry" onClick={handleRetry}>
            Try Again
          </Button>,
          <Button key="contact" href="mailto:support@example.com">
            Contact Support
          </Button>
        ]}
        style={{ background: 'white', borderRadius: 8, padding: 40 }}
      />
    </div>
  );
};

/**
 * Component for unauthorized users (not found in system)
 */
export const UnauthorizedUser: React.FC = () => {
  const { logout } = useAuth0();

  const handleBackToLogin = (): void => {
    (logout as any)(logoutConfig);
  };

  const handleRequestAccess = (): void => {
    // In a real implementation, this would open a request access form
    // For now, we'll just show an alert
    alert('Access request functionality would be implemented here. Please contact your system administrator.');
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: '#f5f5f5',
      padding: 20
    }}>
      <Result
        icon={<UserDeleteOutlined style={{ color: '#ff4d4f' }} />}
        status="403"
        title="Access Denied"
        subTitle="You are authenticated but not authorized to access this application. Your user account may not be set up in the system."
        extra={[
          <Button type="primary" key="request" onClick={handleRequestAccess}>
            Request Access
          </Button>,
          <Button key="logout" onClick={handleBackToLogin}>
            Back to Login
          </Button>
        ]}
        style={{ background: 'white', borderRadius: 8, padding: 40 }}
      >
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Alert
            message="Need Access?"
            description="If you believe you should have access to this system, please contact your system administrator or use the 'Request Access' button above."
            type="warning"
            showIcon
          />
        </div>
      </Result>
    </div>
  );
};

/**
 * Profile completion component (if user profile is incomplete)
 */
export const ProfileIncomplete: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: 20
  }}>
    <Result
      status="warning"
      title="Profile Setup Required"
      subTitle="Your user profile is incomplete. Please complete your profile setup to access the application."
      extra={[
        <Button type="primary" key="complete">
          Complete Profile
        </Button>
      ]}
      style={{ background: 'white', borderRadius: 8, padding: 40 }}
    />
  </div>
);

/**
 * Simple logout component/button
 */
export const LogoutButton: React.FC<{ 
  type?: 'text' | 'link' | 'default' | 'primary' | 'dashed';
  size?: 'large' | 'middle' | 'small';
}> = ({ type = 'default', size = 'middle' }) => {
  const { signOut } = useAuth();
  const { logout: auth0Logout } = useAuth0();
  const [loading, setLoading] = React.useState(false);

  const handleLogout = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    if (loading) return;
    
    try {
      setLoading(true);
      await signOut();
    } catch (error) {
      console.error('Error in signOut:', error);
      setLoading(false);
      // Attempt direct Auth0 logout as fallback
      try {
        (auth0Logout as any)(logoutConfig);
      } catch (logoutError) {
        console.error('Direct logout also failed:', logoutError);
        // Last resort - force navigation
        window.location.href = window.location.origin;
      }
    }
  };

  return (
    <Button 
      type={type} 
      size={size}
      onClick={handleLogout}
      loading={loading}
      disabled={loading}
      style={{ borderRadius: 4 }}
    >
      Sign Out
    </Button>
  );
};

/**
 * User info display component
 */
export const UserInfo: React.FC<{ showEmail?: boolean }> = ({ showEmail = false }) => {
  const { user: auth0User } = useAuth0();

  if (!auth0User) return null;

  return (
    <Space>
      {auth0User.picture && (
        <img 
          src={auth0User.picture} 
          alt={auth0User.name || 'User'} 
          style={{ 
            width: 32, 
            height: 32, 
            borderRadius: '50%',
            objectFit: 'cover'
          }} 
        />
      )}
      <div>
        <Text strong>{auth0User.name || auth0User.nickname}</Text>
        {showEmail && auth0User.email && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {auth0User.email}
            </Text>
          </div>
        )}
      </div>
    </Space>
  );
};